import { FC } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { compact, flatten, isEmpty } from 'lodash';
import { faSearch, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { Button, createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import {
  ComponentSpinner,
  EmptyRecordsMessage,
  FACET,
  formatLanguageName,
  getLocationLabel,
  getURLState,
  Icon,
  MoreToolsDropdown,
  RECORD_TOOLS,
  ResultCountWithSorting,
  SEARCH_OPTION,
  SearchHistoryEntry,
  SelectedFacets,
} from '@app/components';
import { useAppContext } from '@app/contexts';
import { useLocationsFetch } from '@app/hooks';
import {
  generateRouteParams,
  getDateRangeLabel,
  getShouldMapFacetLabel,
} from '@app/utils';
import { resultCountMessages, sharedMessages } from '@app/translations';
import { ROUTE } from '@app/constants';

import { SearchRecordDto } from '../../constants';
import { useDashboardListSorting } from '../../useDashboardListSorting';
import { DashboardRecordCard } from '../DashboardRecordCard';
import { SavedSearchToolButton } from './SavedSearchToolButton';

import '../HistoryRecordsPageWrapper.scss';

type IndexRetrievalFunc = (index: number | string) => void;

export type OnSearchHistoryEntryRemoval = IndexRetrievalFunc;

const cnBem = createBemBlockBuilder(['emptyRecordsMessage']);

interface SearchHistoryListProps {
  list: SearchHistoryEntry[];
  savedSearchesList: SearchHistoryEntry[];
  emptyStateMessage: string;
  emptyStateIcon: IconDefinition;
  itemSubtitle?: string;
  isLoading?: boolean;
  isItemRemovable?: boolean;
  onEntryRemoval: OnSearchHistoryEntryRemoval;
  onAddToProject: () => void;
  onViewInfo: (record: SearchRecordDto) => void;
}

export const SearchHistoryList: FC<SearchHistoryListProps> = ({
  list,
  savedSearchesList,
  emptyStateMessage,
  emptyStateIcon,
  itemSubtitle,
  isLoading = false,
  isItemRemovable,
  onEntryRemoval,
  onAddToProject,
  onViewInfo,
}) => {
  const { $t } = useIntl();
  const { isLoggedIn } = useAppContext();
  const {
    sortDirection,
    sortOptions,
    shouldBeReversed,
    getSortedList,
    handleSortChange,
  } = useDashboardListSorting();
  const locationsQuery = useLocationsFetch();
  const navigate = useNavigate();

  const getItemSubtitle = (index: number) =>
    shouldBeReversed ? `S${index + 1}` : `S${list.length - index}`;

  const getEntryIndex = (index: number) => {
    if (isLoggedIn) {
      return list[index].id ?? '';
    }

    return shouldBeReversed ? list.length - index - 1 : index;
  };

  const getFacetLabels = (facets: SelectedFacets = {}) => {
    return Object.keys(facets).map(facetName => {
      const currentFacetValue = facets[facetName];

      if (!getShouldMapFacetLabel(facetName)) {
        return currentFacetValue;
      }

      if (facetName === FACET.LANGUAGE) {
        return currentFacetValue.map(code => formatLanguageName(code, $t));
      }

      if (facetName === FACET.LOCATION) {
        return currentFacetValue.map(id =>
          getLocationLabel({ id, locations: locationsQuery.data })
        );
      }

      return [];
    });
  };

  if (isLoading) {
    return <ComponentSpinner />;
  }

  return (
    <div data-auto="searches-list">
      {isEmpty(list) ? (
        <EmptyRecordsMessage
          className={cnBem()}
          icon={emptyStateIcon}
          message={emptyStateMessage}
        >
          <Button onClick={() => navigate(ROUTE.search)}>
            <Icon icon={faSearch} />
            {$t(sharedMessages.startANewSearch)}
          </Button>
        </EmptyRecordsMessage>
      ) : (
        <>
          <ResultCountWithSorting
            recordsCount={list.length}
            countMessage={resultCountMessages.numberOfSearches}
            sortField="name"
            sortDirection={sortDirection}
            sortOptions={sortOptions}
            onSortChange={handleSortChange}
          />
          {getSortedList(list).map((entry, index) => {
            const {
              query,
              option = SEARCH_OPTION.keyword,
              facets,
              publicationYear,
              totalRecords = 0,
              searchInterface,
              timestamp,
              recordsPerPage,
              pageNumber,
              sortDirection,
              sortField,
            } = entry;
            const filters = compact([
              ...flatten(getFacetLabels(facets)),
              getDateRangeLabel(publicationYear ?? {}),
            ]);
            const link = generateRouteParams({
              pathname: ROUTE.search,
              query: getURLState({
                query,
                option,
                facets,
                publicationYear,
                recordsPerPage,
                sortField,
                sortDirection,
                pageNumber,
              }),
            });
            const searchOption = $t(sharedMessages[option]);

            return (
              <DashboardRecordCard
                title={query || '*'}
                titleLink={link}
                timestamp={timestamp}
                subTitle={itemSubtitle ?? getItemSubtitle(index)}
                resultsCount={totalRecords}
                key={timestamp}
                tools={
                  <>
                    <SavedSearchToolButton
                      entry={entry}
                      savedSearchesList={savedSearchesList}
                      isItemRemovable={isItemRemovable}
                    />
                    <MoreToolsDropdown
                      tools={[
                        {
                          ...RECORD_TOOLS.details,
                          onClick: () =>
                            onViewInfo({
                              query,
                              option: searchOption,
                              timestamp,
                              filters,
                              totalRecords,
                              searchInterface,
                              link,
                            }),
                        },
                        {
                          ...RECORD_TOOLS.addToProject,
                          onClick: () => onAddToProject(),
                        },
                        {
                          ...RECORD_TOOLS.delete,
                          onClick: () => onEntryRemoval(getEntryIndex(index)),
                        },
                      ]}
                    />
                  </>
                }
                tags={[searchOption, ...filters]}
              />
            );
          })}
        </>
      )}
    </div>
  );
};
